
import React, { useEffect, useRef, useState } from 'react';
import styles from './_lease-quoter.module.scss';
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import LeaseQuoteEditor from './LeaseQuoteEditor/LeaseQuoteEditor';
import LeaseTaxesEditor from './LeaseTaxesEditor/LeaseTaxesEditor';
import LeaseTradesEditor from './LeaseTradesEditor/LeaseTradesEditor';
import { useDispatch, useSelector } from "react-redux";
import {Card, CardContent} from "@mui/material";
import LockNotice from "../../Shared/LockNotice";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import {IConfiguration} from "../../../Data/ConfigurationData";
import {ILookupItems} from "../../../Interfaces/ILookupItems";
import {TabName} from "../../Views/OrderEditor/OrderEditorTabs";
import {ApplicationDispatch, setLeaseQuoteNeedsSolve} from "../../../store/application/types";
import {useBusinessRulesEngine} from "../../../Data/BusinessRulesData";
import {AppState} from "../../../store/rootReducer";
import {
  getLeaseQuoteErrorMessages,
  getQuoteBackgroundColor,
  leaseQuoteFieldIsDisabled,
  getLeaseTermInMonthsForNumberOfPaymentsAndFrequency,
  getLeaseNumberOfPaymentsForTermInMonthsAndFrequency,
} from "../../../utils/quoteUtils";
import {useDialog} from "../../../providers/DialogProvider";
import { usePostedAppLock } from '../../../hooks/usePostedAppLock';
import ErrorDialog from "../../Dialogs/ErrorDialog";
import {StatusWorkflowComponent} from "../../Views/OrderEditor/StatusWorkflowComponent";
import LeaseFeesEditor from "./LeaseFeesEditor/LeaseFeesEditor";
import {updateLeaseQuoteCalcErrorsSuccess} from "../../../store/Notifications/actions";
import produce from "immer";
import {updateEditingApp} from "../../../store/application/actions";
import {ILeaseFee} from "../../../Interfaces/Common/ILeaseFee";
import {
  addDays,
  getNumberOfPaymentsForTermInMonthsAndNumberOfPaymentsPerYear,
  setDateTimeToNoon
} from '@amsanalytics/react-shared-library';
import { useLeaseQuoteSolve } from '../../../hooks/UseLeaseQuoteSolve';
import { getTradeInTotals } from '../../../utils/leaseQuoteSolveUtils';

export interface LeaseQuoterProps {
  configuration: IConfiguration;
  lookups: ILookupItems;
  preventDesking: boolean;
  navigate: (tabName: TabName) => void;
  // classes: Record<"titleText" | "noBorder" | "fontSize" | "contentText", string>;
}

export default function LeaseQuoter(props: LeaseQuoterProps) {
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const applicationLeaseQuoteIsDirty = useSelector((state: AppState) => state.applications.applicationLeaseQuoteIsDirty!);
  const applicationLeaseQuoteNeedsSolve = useSelector((state: AppState) => state.applications.applicationLeaseQuoteNeedsSolve);
  const applicationLeaseQuote = currentApp.applicationLeaseQuote;
  const [selectedTab, setSelectedTab] = useState(0);
  const [openDialog, closeDialog] = useDialog();
  const { isSectionLocked, lockMessage } = usePostedAppLock();
  const { resolveQuietly } = useLeaseQuoteSolve();
  const leaseLoanClasses = props.lookups.leaseLoanClasses;
  const selectedLeaseLoanClass = leaseLoanClasses.find(flc => flc.id === applicationLeaseQuote?.leaseLoanClassId);
  const applicationLeaseErrors = useSelector((state: AppState) => state.notifications.leaseQuoteErrors);
  const leaseErrorMessages = getLeaseQuoteErrorMessages(applicationLeaseErrors);
  const applicationLeaseQuoteCalcErrors = useSelector((state: AppState) => state.notifications.leaseQuoteCalcErrors);
  const disabledFields = props.configuration.leaseQuoterDisabledFields;
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const quotePayTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  const selectedPaymentFrequency = quotePayTypes.find(qpt => qpt.id === applicationLeaseQuote?.paymentFrequencyId);

  const getLoanClassById = (loanClassId: string | undefined) => {
    return props.lookups.leaseLoanClasses.find(lc => lc.id === loanClassId);
  }

  const getLeaseFeeByDescription = (leaseFees: ILeaseFee[], leaseFeeDescription: string): ILeaseFee | undefined => {
    return leaseFees?.find(lf => lf.description.trim() === leaseFeeDescription.trim());
  }

  const updateLoanClassAndFees = (newLoanClassId: string | undefined) => {
    let newLoanClass = getLoanClassById(newLoanClassId);
    const newLeaseFees = props.lookups.leaseFees.filter(lf => lf.loanClassCode === newLoanClass?.code);

    if (!newLoanClass && props.lookups.leaseLoanClasses.length > 0) {
      newLoanClass = props.lookups.leaseLoanClasses[0];
    }

    const newPaymentFrequency = quotePayTypes.find(qpt => qpt.id === newLoanClass?.defaultPaymentFrequencyId)!;
    const defaultNumberOfPaymentsForLoanClass = getNumberOfPaymentsForTermInMonthsAndNumberOfPaymentsPerYear(newLoanClass?.defaultTermInMonths ?? 0, newPaymentFrequency.paymentsPerYear);

    const updatedApp = produce(currentApp, newCurrentApp => {
      if (!applicationLeaseQuote) {
        const today = new Date();

        newCurrentApp.applicationLeaseQuote = {
          applicationId: currentApp.id,
          leaseLoanClassId: newLoanClassId,
          paymentFrequencyId: newPaymentFrequency.id,
          totalDownRollback: 0,
          contractDate: today,
          firstPmtDate: addDays(today, 30),
          apr: 0,
          residualPercent: 0,
          sellingPrice: currentApp.vehicle?.carPrice ?? 0,
          numberOfPmts: defaultNumberOfPaymentsForLoanClass,
          termInMonths: newLoanClass?.defaultTermInMonths ?? 0,
          stateTaxRate: 0,
          stateTaxRate2: 0,
          parishCountyTaxRate: 0,
          parishCountyTaxRate2: 0,
          cityTaxRate: 0,
          cityTaxRate2: 0,
          tradeAllowance: (currentApp.tradeInVehicle?.tradeValue ?? 0) - (currentApp.tradeInVehicle?.tradeBalance ?? 0),
          cashDown: 0,
          securityDeposit: 0,
          mileage: 0,
          gapFee: 0,
          docFee: 0,
          titleFee: 0,
          licenseFee: 0,
          batteryFee: 0,
          transferFee: 0,
          deliveryFee: 0,
          serviceWarranty: 0,
          dispositionFee: 0,
          acquisitionFee: 0,
          discountPercent: 0,
          tavt: 0,
          customFee1: 0,
          customFee2: 0,
          customFee3: 0,
          modifiedBy: activeUser?.id ?? "",
          modifiedDate: new Date()
        }
      }

      // Updates Non-standard lease fees
      newCurrentApp.applicationLeaseQuote.customFee1Description = null;
      newCurrentApp.applicationLeaseQuote.customFee2Description = null;
      newCurrentApp.applicationLeaseQuote.customFee3Description = null;
      newLeaseFees?.filter(lf => !lf.standardFee)?.forEach(lf => {
        switch (lf.sortOrder) {
          case 1:
            newCurrentApp.applicationLeaseQuote.customFee1 = lf.amount ?? 0;
            newCurrentApp.applicationLeaseQuote.customFee1Description = lf.description;
            break;
          case 2:
            newCurrentApp.applicationLeaseQuote.customFee2 = lf.amount ?? 0;
            newCurrentApp.applicationLeaseQuote.customFee2Description = lf.description;
            break;
          case 3:
            newCurrentApp.applicationLeaseQuote.customFee3 = lf.amount ?? 0;
            newCurrentApp.applicationLeaseQuote.customFee3Description = lf.description;
            break;
        }
      });

      // Updates the Standard Lease Values
      newCurrentApp.applicationLeaseQuote.apr = newLoanClass?.defApr ?? 0;
      newCurrentApp.applicationLeaseQuote.residualPercent = newLoanClass?.residPerc ?? 0;
      newCurrentApp.applicationLeaseQuote.mileage = newLoanClass?.baseMiles ?? 0;
      newCurrentApp.applicationLeaseQuote.leaseLoanClassId = newLoanClass?.id ?? '';
      newCurrentApp.applicationLeaseQuote.stateTaxRate = newLoanClass?.defSalesTaxRate ?? 0;

      if (!newLoanClass?.dualTaxes) {
        // Clears out the tax rates if they pick a loan class that doesn't do dual taxes
        newCurrentApp.applicationLeaseQuote.stateTaxRate2 = 0;
        newCurrentApp.applicationLeaseQuote.parishCountyTaxRate2 = 0;
        newCurrentApp.applicationLeaseQuote.cityTaxRate2 = 0;
      }

      // Values from the LeaseFee table
      newCurrentApp.applicationLeaseQuote.batteryFee = getLeaseFeeByDescription(newLeaseFees, 'BATTERY FEE')?.amount ?? 0;
      newCurrentApp.applicationLeaseQuote.deliveryFee = getLeaseFeeByDescription(newLeaseFees, 'DELIVERY FEE')?.amount ?? 0;
      newCurrentApp.applicationLeaseQuote.docFee = getLeaseFeeByDescription(newLeaseFees, 'DOC FEE')?.amount ?? 0;
      newCurrentApp.applicationLeaseQuote.gapFee = getLeaseFeeByDescription(newLeaseFees, 'GAP FEE')?.amount ?? 0;
      newCurrentApp.applicationLeaseQuote.licenseFee = getLeaseFeeByDescription(newLeaseFees, 'LICENSE FEE')?.amount ?? 0;
      const newServWarrFeeAmount = getLeaseFeeByDescription(newLeaseFees, 'SERV WARR FEE')?.amount;
      newCurrentApp.applicationLeaseQuote.serviceWarranty = newServWarrFeeAmount
        ? newServWarrFeeAmount
        : (newLoanClass?.servwarrCost ?? 0);
      newCurrentApp.applicationLeaseQuote.titleFee = getLeaseFeeByDescription(newLeaseFees, 'TITLE FEE')?.amount ?? 0;
      newCurrentApp.applicationLeaseQuote.transferFee = getLeaseFeeByDescription(newLeaseFees, 'TRANSFER FEE')?.amount ?? 0;
    });

    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  };

  // Runs once in the beginning to create the lease quote
  useEffect(() => {
    if (props.lookups.leaseLoanClasses.length > 0 && !applicationLeaseQuote) {
      updateLoanClassAndFees(props.lookups.leaseLoanClasses[0].id);
    }
  }, [props.lookups.leaseLoanClasses.length, applicationLeaseQuoteIsDirty]);

  const quoteBackgroundColor = getQuoteBackgroundColor(applicationLeaseQuoteIsDirty || applicationLeaseQuoteNeedsSolve);

  // A solved quote re-solves when a trade-in's value or payoff changes, as the finance quoter does;
  // when it cannot (locked, not allowed, invalid) it is marked as needing a Solve.
  const tradeInTotals = getTradeInTotals(currentApp);
  const lastTradeInTotals = useRef<{ applicationId: string; totals: string } | undefined>(undefined);
  useEffect(() => {
    const totals = `${tradeInTotals.value}|${tradeInTotals.payoff}`;
    const previous = lastTradeInTotals.current;
    lastTradeInTotals.current = { applicationId: currentApp.id, totals };
    // Seeded on the first run and when another application is opened in place.
    if (previous?.applicationId !== currentApp.id || previous.totals === totals || !applicationLeaseQuote) {
      return;
    }
    if (!resolveQuietly(currentApp)) {
      dispatch(setLeaseQuoteNeedsSolve());
    }
  }, [currentApp.id, tradeInTotals.value, tradeInTotals.payoff]);

  const leaseQuoteFieldIsDisabledInQuoter = (fieldName: string): boolean => {
    if (isSectionLocked('Quoter')) return true;
    return leaseQuoteFieldIsDisabled(disabledFields, fieldName);
  }

  useEffect(() => {
    if (applicationLeaseQuoteCalcErrors.length > 0) {
      openDialog({
        children: (
          <ErrorDialog
            title="Quote Calculation Errors"
            errors={applicationLeaseQuoteCalcErrors}
            closeDialog={() => {
              dispatch(updateLeaseQuoteCalcErrorsSuccess([]) as any);
              closeDialog();
            }}
          />
        ),
      });
    }
  }, [applicationLeaseQuoteCalcErrors]);

  const updateApplicationLeaseQuote = (fieldName: string, value: number | Date | string | null | undefined) => {
    const updatedApp = produce(currentApp, updatedCurrentApp => {
      const applicationLeaseQuoteToUpdate = updatedCurrentApp.applicationLeaseQuote;

      if (value instanceof Date) {
        applicationLeaseQuoteToUpdate[fieldName] = setDateTimeToNoon(new Date(value));
      } else {

        switch (fieldName) {
          case "numberOfPmts":
            const newTermInMonths = getLeaseTermInMonthsForNumberOfPaymentsAndFrequency(value as number, selectedPaymentFrequency?.paymentsPerYear);
            applicationLeaseQuoteToUpdate.termInMonths = selectedPaymentFrequency?.paymentsPerYear === 1
              ? 1
              : newTermInMonths;
            applicationLeaseQuoteToUpdate.numberOfPmts = selectedPaymentFrequency?.paymentsPerYear === 1
              ? 1
              : value as number;
            break;
          case "termInMonths":
            const paymentsPerYearFromQuote = quotePayTypes.find(qpt => qpt.id === applicationLeaseQuoteToUpdate.paymentFrequencyId)?.paymentsPerYear;
            applicationLeaseQuoteToUpdate.numberOfPmts = paymentsPerYearFromQuote === 1
              ? 1
              : getLeaseNumberOfPaymentsForTermInMonthsAndFrequency(value as number, paymentsPerYearFromQuote);
            applicationLeaseQuoteToUpdate.termInMonths = paymentsPerYearFromQuote === 1
              ? 1
              : value as number;
            break;
          case "paymentFrequencyId":
            const newPaymentsPerYear = quotePayTypes.find(qpt => qpt.id === value)?.paymentsPerYear;
            applicationLeaseQuoteToUpdate.numberOfPmts = newPaymentsPerYear === 1
              ? 1
              : getLeaseNumberOfPaymentsForTermInMonthsAndFrequency(applicationLeaseQuoteToUpdate.termInMonths, newPaymentsPerYear);
            applicationLeaseQuoteToUpdate.paymentFrequencyId = value?.toString() ?? '';
            applicationLeaseQuoteToUpdate.termInMonths = applicationLeaseQuoteToUpdate.numberOfPmts === 1
              ? 1
              : applicationLeaseQuoteToUpdate.termInMonths;
            break;
          default:
            applicationLeaseQuoteToUpdate[fieldName] = value;
            break;
        }
      }
    })
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }

  return (
    <div>
      {isSectionLocked('Quoter') && <LockNotice message={lockMessage} />}
      {applicationLeaseQuote &&
        <Card>
          <CardHeader
            avatar={<AssignmentIcon fontSize="small" />}
            title="Quoter"
          />
          <Divider variant="middle" />
          <CardContent className={styles.leaseQuoterContainer} style={{padding: 0, marginTop: 0}}>
            <TabStrip
              selected={selectedTab}
              onSelect={(e: TabStripSelectEventArguments) => setSelectedTab(e.selected)}
              style={{ backgroundColor: quoteBackgroundColor }}
            >
              <TabStripTab title="Quote">
                <LeaseQuoteEditor
                  {...props}
                  currentApp={currentApp}
                  selectedLeaseLoanClass={selectedLeaseLoanClass}
                  onChangeSingleField={updateApplicationLeaseQuote}
                  applicationLeaseErrors={applicationLeaseErrors}
                  leaseQuoteFieldIsDisabled={leaseQuoteFieldIsDisabledInQuoter}
                  selectedPaymentFrequency={selectedPaymentFrequency}
                  handleChangeSelectedLeaseLoanClass={updateLoanClassAndFees}
                />
              </TabStripTab>
              <TabStripTab title="Fees">
                <LeaseFeesEditor
                  {...props}
                  selectedLeaseLoanClass={selectedLeaseLoanClass}
                  leaseQuoteFieldIsDisabled={leaseQuoteFieldIsDisabledInQuoter}
                  currentApp={currentApp}
                  errorMessage={leaseErrorMessages.fees}
                />
              </TabStripTab>
              <TabStripTab title="Taxes">
                <LeaseTaxesEditor
                  {...props}
                  currentApp={currentApp}
                  leaseQuoteFieldIsDisabled={leaseQuoteFieldIsDisabledInQuoter}
                  selectedLeaseLoanClass={selectedLeaseLoanClass}
                  errorMessage={leaseErrorMessages.taxes}
                />
              </TabStripTab>
              <TabStripTab title="Trades">
                <LeaseTradesEditor
                  {...props}
                  currentApp={currentApp}
                  leaseQuoteFieldIsDisabled={(fieldName) => isSectionLocked('Vehicle') || leaseQuoteFieldIsDisabledInQuoter(fieldName)}
                  errorMessage={leaseErrorMessages.tradeInVehicles}
                />
              </TabStripTab>
            </TabStrip>
          </CardContent>
        </Card>
      }
      <StatusWorkflowComponent {...props} disableDeskingOrPosting={props.preventDesking} />
    </div>
  );
}
